import { dbtNodeName, dbtResultRows, isDbtUniqueId } from "@shared/types/dbtCauses";
import { importCauseEntry, parseImportCause } from "@shared/types/importCauses";
import {
  STEP_EVENT_PREFIXES,
  STEP_NAMES,
  type RunEventRow,
  type StepName,
} from "@shared/types/runs";
import {
  STEP_LABELS,
  formatCount,
  formatStageSeconds,
  runStatusPresentation,
} from "@/components/runs/runFormatting";
import { noRowsEventSentence } from "@shared/runs/noRows";
import { leftOutSentence, parseLeftOut } from "./leftOut";

/**
 * One readable sentence per run event (Session 10, P11; ux-s10.md §1, map-runlog G5).
 *
 * The run log used to show the raw code (`LOAD_ENTITY_FINISHED`) and a JSON dump. This is the
 * catalogue that turns a code and its payload into a sentence a person reads, keyed on the codes
 * the runner, the web and the connector images write (map-runlog §3). The raw code and the JSON
 * stay behind "Show detail" for support.
 *
 * Rules:
 * - A sentence uses only identifiers, counts, durations and enums from the payload (SEC-17: the
 *   payload holds nothing else), never free text, so a sentence cannot leak a row value.
 * - A count nobody reported is left out of the sentence, never written as zero.
 * - **An unknown code shows itself**: a code this catalogue has not met (a new connector event,
 *   say) reads as the code, so nothing is ever hidden behind a guess.
 * - "import" is the verb (copy rule).
 */

type Payload = Record<string, unknown>;

function payloadOf(row: Pick<RunEventRow, "payload">): Payload {
  const payload = row.payload;
  return payload !== null && typeof payload === "object" && !Array.isArray(payload)
    ? (payload as Payload)
    : {};
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function count(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

function isStepName(value: unknown): value is StepName {
  return typeof value === "string" && (STEP_NAMES as readonly string[]).includes(value);
}

function plural(value: number, one: string, many: string): string {
  return `${formatCount(value)} ${value === 1 ? one : many}`;
}

/** "Orders: " for a table-level event, "" for a run-level one. */
function subject(row: Pick<RunEventRow, "entityName">): string {
  return row.entityName ? `${row.entityName}: ` : "";
}

/** A sentence that starts with a table name keeps it; otherwise the first letter is capital. */
function withSubject(row: Pick<RunEventRow, "entityName">, rest: string): string {
  const head = subject(row);
  if (head) return `${head}${rest}`;
  return rest.charAt(0).toUpperCase() + rest.slice(1);
}

/**
 * The step an event belongs to: its payload's `step` when it names one (every `STEP_*` event,
 * `STEP_RETRY_REQUESTED`, `TRANSFORM_SKIPPED`), else the step whose prefix the code starts with
 * (`STEP_EVENT_PREFIXES`, the same map the server's `step` filter uses), else none (run-level).
 */
export function eventStep(row: Pick<RunEventRow, "event" | "payload">): StepName | null {
  const named = payloadOf(row).step;
  if (isStepName(named)) return named;
  for (const step of STEP_NAMES) {
    if (STEP_EVENT_PREFIXES[step].some((prefix) => row.event.startsWith(prefix))) return step;
  }
  return null;
}

/** "attempt 2" only when it is not the first. */
function attemptSuffix(payload: Payload): string {
  const attempt = count(payload.attempt);
  return attempt !== null && attempt > 1 ? ` (attempt ${attempt})` : "";
}

function stepName(payload: Payload): string {
  return isStepName(payload.step) ? STEP_LABELS[payload.step] : "A step";
}

function durationPart(payload: Payload): string {
  const ms = count(payload.durationMs);
  return ms === null ? "" : ` in ${formatStageSeconds(ms / 1000)}`;
}

/** #2404: " in 3.4 s" from a table's summed statement time; "" when the row carries none. */
function loadDurationPart(payload: Payload): string {
  const ms = count(payload.loadDurationMs);
  return ms === null ? "" : ` in ${formatStageSeconds(ms / 1000)}`;
}

const DBT_STATUS_WORDS: Record<string, string> = {
  success: "built",
  pass: "passed",
  error: "failed",
  fail: "failed",
  "runtime error": "failed",
  skipped: "was skipped",
  warn: "passed with a warning",
  "no-op": "had nothing to do",
  "partial success": "partly built",
};

/** #988: what the run is, so a dbt jobs only run reads as a transformation run, not an import. */
export interface EventScope {
  transformOnly?: boolean;
  /** #1673: connection names by id, so `RUN_CREATED` names the connections it left out. */
  connectionNames?: ReadonlyMap<string, string>;
}

type SentenceBuilder = (row: RunEventRow, payload: Payload, scope: EventScope) => string;

/**
 * Product gaps row 4: " (No permission on a table)" when the event carries a cause code of the
 * given source, the catalogue's label; empty otherwise, so an older event reads as it did.
 */
function causePart(payload: Payload, source: "connector" | "load"): string {
  const cause = parseImportCause({ source, code: payload.causeCode });
  return cause ? ` (${importCauseEntry(cause).label})` : "";
}

const SENTENCES: Record<string, SentenceBuilder> = {
  /* The extract (connector images, through the progress drain). */
  EXTRACT_STARTED: () => "The extract started.",
  EXTRACT_COMPLETED: () => "The extract finished.",
  EXTRACT_START_ERROR: (_row, payload) =>
    payload.lastAttempt === true
      ? "The extract could not start, and this was its last attempt."
      : "The extract could not start. It will be tried again.",
  // 2026-09-30: an incremental connection none of whose tables has landed yet reads everything
  // once (review r1: a connection with at least one landed table stays incremental). Review r2:
  // a run scoped to tables none of which has landed, or each asked to re-sync, reads them in full.
  REFRESH_FELL_BACK_TO_FULL: (_row, payload) => {
    if (payload.reason === "no_bound_in_scope") {
      return "Incremental was chosen, but each table in this import either has no earlier import or was asked to re-sync, so this import reads those tables in full.";
    }
    // #2521: a confirmed destination move cleared every bound of a connection that had imported.
    if (payload.reason === "destination_reset") {
      return "The destination moved, so this import reads its tables in full into the new place.";
    }
    return (
      // Round 6 (owner, 2026-10-01 23:40, binding): the first incremental run's own words.
      "Imported everything: this connection had no previous import. The next import continues from this one."
    );
  },
  // Round 9 (B-61): the connector's schema and ordering events had no sentence and showed as codes.
  TABLE_ORDER_RESOLVED: () => "The order the tables are read in was settled.",
  TABLE_COLUMN_TYPES: (row) => withSubject(row, "column types recorded."),
  EXTRACT_ALREADY_RUNNING: () => "The extract was already running, so no second one was started.",
  TABLE_STARTED: (row) => withSubject(row, "extract started."),
  TABLE_COMPLETED: (row, payload) => {
    const records = count(payload.records);
    return withSubject(
      row,
      records === null ? "extracted." : `${plural(records, "row", "rows")} extracted.`,
    );
  },
  TABLE_FAILED: (row, payload) =>
    withSubject(row, `the extract failed${causePart(payload, "connector")}.`),
  // #2389: the periodic rows-read-so-far tick writes no event row, but a runner without #2389
  // writes one per tick, so a row an older drain wrote reads as words, never the raw code.
  TABLE_PROGRESS: (row, payload) => {
    const records = count(payload.records);
    return withSubject(
      row,
      records === null ? "rows being read." : `${plural(records, "row", "rows")} read so far.`,
    );
  },
  TABLE_SKIPPED: (row) => withSubject(row, "skipped by the connector."),
  // Go-live 2026-10-19: a system object of the source database (`sys.database_firewall_rules`)
  // stored as selected before the rule existed is never imported, and the run goes on.
  TABLE_SYSTEM_OBJECT_SKIPPED: (row) =>
    withSubject(
      row,
      "skipped. It is a system object of the source database, and system objects are never imported. Validate the connection to remove it from the selected tables.",
    ),
  TABLE_RETRY_SCHEDULED: (row) => withSubject(row, "the extract will be tried again."),

  /* The load (`runner/steps/loadSnowflake.ts`, `startLoad.ts`). */
  LOAD_STARTED: (_row, payload) => {
    const entities = count(payload.entities);
    return entities === null
      ? "The load started."
      : `The load started for ${plural(entities, "table", "tables")}.`;
  },
  LOAD_STAGE: (_row, payload) =>
    text(payload.stage)
      ? `The load stage ${String(payload.stage)} is ready.`
      : "The load stage is ready.",
  LOAD_STAGE_SKIPPED: () => "The load stage was left as it is.",
  // #1672: the push job's start and failure (`runner/steps/pushLoad.ts`), now live frames too.
  PUSH_STARTED: () => "Sending the imported files to the warehouse.",
  PUSH_FAILED: (_row, payload) =>
    payload.code === "PUSH_SIZE_MISMATCH"
      ? "Sending the imported files to the warehouse failed. A file's size did not match."
      : "Sending the imported files to the warehouse failed.",
  // #514, F52: the push job's end (`runner/steps/pushLoad.ts`); an empty part may be a truncated one.
  PUSH_FINISHED: (_row, payload) => {
    const skipped = count(payload.skippedEmpty);
    const sent = "The imported files were sent to the warehouse.";
    if (!skipped) return sent;
    return `${sent} ${plural(skipped, "empty file was", "empty files were")} skipped.`;
  },
  // F53: the load was stopped (a cancel, a disabled tenant) while the push job ran.
  PUSH_STOPPED: () => "Sending the imported files to the warehouse was stopped.",
  LOAD_STAGE_FAILED: () => "The load stage could not be prepared.",
  LOAD_STATEMENT: (row, payload) => {
    const statement = text(payload.statement) ?? "A statement";
    return withSubject(row, `${statement} ran${durationPart(payload)}.`);
  },
  LOAD_STATEMENT_FAILED: (row, payload) =>
    withSubject(row, `${text(payload.statement) ?? "a statement"} failed.`),
  LOAD_STATEMENT_SKIPPED: (row, payload) =>
    withSubject(row, `${text(payload.statement) ?? "a statement"} was not needed.`),
  LOAD_ENTITY_FINISHED: (row, payload) => {
    const rows = count(payload.rowsLoaded);
    const target = text(payload.targetTable);
    if (payload.status === "skipped") return withSubject(row, "not loaded: no destination.");
    const into = target ? ` into ${target}` : "";
    // 2026-09-30: a window load replaced only the days the incremental import re-read.
    const window = payload.loadMode === "replace_window" ? ", replacing the days it re-read" : "";
    // #2404: the table's statements are listed on this one row (`statementLog`, Show detail), and
    // their summed time is its time. A row stored before it has none, and reads as it did.
    const took = loadDurationPart(payload);
    return withSubject(
      row,
      rows === null
        ? `loaded${into}${took}${window}.`
        : `${plural(rows, "row", "rows")} loaded${into}${took}${window}.`,
    );
  },
  LOAD_ENTITY_FAILED: (row, payload) => {
    const rows = count(payload.rowsLoaded);
    const expected = count(payload.expectedRows);
    return withSubject(
      row,
      rows !== null && expected !== null
        ? `the load failed: ${formatCount(rows)} of ${plural(expected, "expected row", "expected rows")} loaded.`
        : `the load failed${causePart(payload, "load")}.`,
    );
  },
  // #2392 (review): a table the source sent nothing for reads as the Tables tab names it.
  LOAD_ENTITY_SKIPPED: (row, payload) =>
    withSubject(row, noRowsEventSentence(payload.noRows) ?? "not loaded."),
  // Round 5: a table an earlier build loaded under its folded name (`dbocustomers`) is renamed
  // in place to the name that keeps the schema separator (`dbo_customers`). Identifiers only.
  // Round 6 retired the rename; the sentence stays for the runs that recorded one.
  SNOWFLAKE_TABLE_RENAMED: (row, payload) => {
    const from = text(payload.previousTable);
    const to = text(payload.targetTable);
    return withSubject(
      row,
      from && to
        ? `the table ${from} was renamed to ${to}, keeping its rows.`
        : "the table was renamed to its new name, keeping its rows.",
    );
  },
  LOAD_SCHEMA_DRIFT: (row) => withSubject(row, "the source columns changed since the last load."),
  // Product gaps row 7: the schema change policy. Counts only; the columns are in the change log.
  LOAD_SCHEMA_HELD: (row, payload) => {
    const columns = count(payload.columnCount);
    const held = columns === null ? "new columns" : plural(columns, "new column", "new columns");
    return withSubject(row, `${held} waiting for review, not added and not loaded.`);
  },
  LOAD_SCHEMA_WIDENED: (row, payload) => {
    const columns = count(payload.columnCount);
    return withSubject(
      row,
      `${columns === null ? "columns" : plural(columns, "column", "columns")} widened before the load.`,
    );
  },
  LOAD_SCHEMA_REBUILT: (row) => withSubject(row, "the table was rebuilt from a full read."),
  LOAD_FINISHED: (_row, payload) => {
    const entities = count(payload.entities);
    return entities === null
      ? "The load finished."
      : `The load finished for ${plural(entities, "table", "tables")}.`;
  },
  LOAD_ERROR: (_row, payload) =>
    payload.lastAttempt === true
      ? "The load could not run, and this was its last attempt."
      : "The load could not run. It will be tried again.",

  /* The transform (the dbt image and the runner). */
  TRANSFORM_STARTED: () => "The dbt jobs started.",
  TRANSFORM_MODEL_STARTED: (row) => withSubject(row, "model started."),
  TRANSFORM_MODEL_COMPLETED: (row) => withSubject(row, "model finished."),
  TRANSFORM_MODEL_FAILED: (row) => withSubject(row, "model failed."),
  TRANSFORM_TEST_COMPLETED: (_row, payload) => {
    const tests = count(payload.testsTotal);
    return tests === null
      ? "The tests finished."
      : `The tests finished: ${plural(tests, "test", "tests")} ran.`;
  },
  TRANSFORM_COMPLETED: () => "The dbt jobs finished.",
  TRANSFORM_START_ERROR: (_row, payload) =>
    payload.lastAttempt === true
      ? "The dbt jobs could not start, and this was their last attempt."
      : "The dbt jobs could not start. They will be tried again.",
  TRANSFORM_MODEL_SKIPPED: () => "A dbt job was skipped: it has no dbt selector.",
  TRANSFORM_ALREADY_RUNNING: () =>
    "The dbt jobs were already running, so no second run was started.",
  TRANSFORM_SKIPPED: () => "The dbt jobs were skipped.",
  DBT_MODEL: (_row, payload) => {
    const node = isDbtUniqueId(payload.node) ? dbtNodeName(payload.node) : "A model";
    const word = DBT_STATUS_WORDS[String(payload.status)] ?? "finished";
    const seconds =
      typeof payload.executionTimeSeconds === "number" ? payload.executionTimeSeconds : null;
    // #1093: a view or ephemeral node writes no rows; the 1 dbt reports is its DDL statement.
    // #1443: a failed or skipped result wrote none in its run, whatever count it carries.
    const rows = dbtResultRows(
      payload.status,
      payload.materialization,
      count(payload.rowsAffected),
    );
    const parts = [`Model ${node} ${word}`];
    if (seconds !== null) parts.push(`in ${formatStageSeconds(seconds)}`);
    const tail = rows !== null ? `, ${plural(rows, "row", "rows")} affected` : "";
    return `${parts.join(" ")}${tail}.`;
  },
  DBT_TEST: (_row, payload) => {
    const node = isDbtUniqueId(payload.node) ? dbtNodeName(payload.node) : "A test";
    const word = DBT_STATUS_WORDS[String(payload.status)] ?? "finished";
    const failures = count(payload.failures);
    const failing =
      word === "failed" && failures !== null && failures > 0
        ? ` with ${plural(failures, "failure", "failures")}`
        : "";
    return `Test ${node} ${word}${failing}.`;
  },

  /* The step chain and the run (the runner and the web). */
  STEP_PENDING: (_row, payload) => `${stepName(payload)} is waiting${attemptSuffix(payload)}.`,
  STEP_RUNNING: (_row, payload) => `${stepName(payload)} started${attemptSuffix(payload)}.`,
  STEP_SUCCEEDED: (_row, payload) => `${stepName(payload)} succeeded${attemptSuffix(payload)}.`,
  STEP_FAILED: (_row, payload) => `${stepName(payload)} failed${attemptSuffix(payload)}.`,
  STEP_SKIPPED: (_row, payload) => `${stepName(payload)} was skipped.`,
  STEP_CANCELLED: (_row, payload) => `${stepName(payload)} was cancelled.`,
  STEP_RETRY_REQUESTED: (_row, payload) => {
    const attempt = count(payload.attempt);
    return attempt === null
      ? `Retry requested: ${stepName(payload)} runs again.`
      : `Retry requested: ${stepName(payload)} runs again as attempt ${attempt}.`;
  },
  // The first event of every run started from the page or the API (`RunService.start`): ids,
  // counts and flags only, so the sentence says who asked and how much, never a name.
  RUN_CREATED: (_row, payload, ctx) => {
    if (ctx.transformOnly || payload.transformOnly === true) {
      return payload.kind === "on_demand"
        ? "The transformation run was requested by hand."
        : "The transformation run was requested.";
    }
    const head =
      payload.kind === "on_demand"
        ? "The import was requested by hand"
        : "The import was requested";
    let scope = "";
    if (payload.transformOnly === true) {
      scope = ": dbt jobs only";
    } else if (Array.isArray(payload.dataflowIds) && payload.dataflowIds.length > 0) {
      scope = ` for ${plural(payload.dataflowIds.length, "connection", "connections")}`;
    }
    const full = payload.fullRefresh === true ? ", as a full refresh" : "";
    // #1673: a schedule's Run now names each connection it left out, and why.
    const leftOut = leftOutSentence(parseLeftOut(payload.leftOut), (id) =>
      ctx.connectionNames?.get(id),
    );
    return leftOut ? `${head}${scope}${full}. ${leftOut}` : `${head}${scope}${full}.`;
  },
  RUN_RUNNING: (_row, _payload, scope) =>
    scope.transformOnly ? "The transformation run started." : "The import started.",
  RUN_FINISHED: (_row, payload, scope) => {
    const what = scope.transformOnly ? "The transformation run" : "The import";
    const status = text(payload.status);
    const known =
      status && ["succeeded", "failed", "partially_succeeded", "cancelled"].includes(status);
    return known
      ? `${what} finished: ${runStatusPresentation(status as "succeeded").label}.`
      : `${what} finished.`;
  },
  RUN_CANCELLED: (_row, payload, scope) => {
    const stopped = count(payload.stopped);
    const what = scope.transformOnly ? "The transformation run" : "The import";
    const base =
      stopped === null
        ? `${what} was cancelled.`
        : `${what} was cancelled. ${plural(stopped, "running job was", "running jobs were")} stopped.`;
    // F53: a stop the platform refused is said, never left to read as "nothing to stop".
    const notStopped = count(payload.notStopped);
    if (!notStopped) return base;
    return `${base} ${plural(notStopped, "job", "jobs")} could not be stopped and may run to ${notStopped === 1 ? "its" : "their"} own end. An import's output is not loaded.`;
  },
  RUN_FORCE_RESET: (_row, payload) => {
    const steps = count(payload.resetSteps);
    return steps === null
      ? "The import was force reset."
      : `The import was force reset. ${plural(steps, "step was", "steps were")} marked cancelled.`;
  },
  RUN_QUEUED_CAPACITY: (_row, payload) => {
    const ahead = count(payload.queuedAhead);
    return ahead === null || ahead < 1
      ? "Waiting to start: the import limit is reached."
      : `Waiting to start: ${plural(ahead, "import", "imports")} ahead of this one.`;
  },
  RUN_ADMITTED_CAPACITY: () => "A slot is free. The import can start.",
  // #1651: a load's push waits for a slot of the same import limit (runner/scheduler/capacity.ts).
  RUN_QUEUED_LOAD: (_row, payload) => {
    const ahead = count(payload.queuedAhead);
    return ahead === null || ahead < 1
      ? "Waiting to load: the import limit is reached."
      : `Waiting to load: ${plural(ahead, "load", "loads")} ahead of this one.`;
  },
  RUN_ADMITTED_LOAD: () => "A slot is free. The load can start.",
  // Round 6, T1: the instance's one dbt slot (runner/scheduler/capacity.ts).
  RUN_QUEUED_TRANSFORM: (_row, payload) => {
    const holder = text(payload.holderRunId);
    return holder
      ? `Waiting to transform: run ${holder} is transforming now, and dbt runs one at a time on an instance.`
      : "Waiting to transform: dbt runs one at a time on an instance.";
  },
  RUN_ADMITTED_TRANSFORM: () => "The run in progress finished. This run's dbt jobs can start.",
  // B-60: a firing of ANOTHER schedule that came due while this import held the connection is
  // not "a later firing" (it can be due in the same minute); name it.
  // The key versions each step read (identifiers only, never a value): which rotation took effect.
  SECRET_VERSIONS_USED: (_row, payload) => {
    const versions = payload.keyVaultVersions;
    const n =
      versions !== null && typeof versions === "object" && !Array.isArray(versions)
        ? Object.keys(versions).length
        : null;
    const what =
      n === null || n < 1
        ? "which saved versions of the credentials"
        : `which saved version of ${plural(n, "credential", "credentials")}`;
    return `skyData recorded ${what} this run used. These are Key Vault version ids, not the credentials themselves.`;
  },
  SCHEDULE_SKIPPED_OVERLAP: (_row, payload) => {
    const name = text(payload.scheduleName);
    // Round 10 wave 2 review: a partial firing ran; it only left out what this import held.
    if (payload.partial === true) {
      return name
        ? `The schedule ${name} was due and left out the connection this import was using. Its other connections were imported.`
        : "A schedule was due and left out the connection this import was using. Its other connections were imported.";
    }
    return payload.sameSchedule === false && name
      ? `The schedule ${name} was due and skipped its firing: this import was using the connection.`
      : "A later firing of the schedule was skipped: this import was still running.";
  },
  // Round 10 wave 2 (A1): this firing ran its free connections and left out the busy ones.
  SCHEDULE_CONNECTIONS_SKIPPED: (_row, payload) => {
    const ids = Array.isArray(payload.dataflowIds) ? payload.dataflowIds : [];
    const holders = Array.isArray(payload.holders) ? payload.holders : [];
    const names = [
      ...new Set(
        holders
          .map((holder: unknown) =>
            holder !== null && typeof holder === "object"
              ? text((holder as Record<string, unknown>).holderScheduleName)
              : null,
          )
          .filter((name): name is string => name !== null),
      ),
    ];
    const what =
      ids.length > 0
        ? `${plural(ids.length, "connection", "connections")} another import was using`
        : "connections another import was using";
    const by = names.length > 0 ? ` (the schedule ${names.join(", ")})` : "";
    return `This firing left out ${what}${by}. The others were imported. They are imported again on a later firing.`;
  },
  EXECUTION_ORPHAN_STOPPED: () => "A job that was left running was stopped.",
  PROGRESS_DEAD_LETTER: () => "A progress message could not be read and was set aside.",
};

/** True when the catalogue has a sentence for this code. */
export function isKnownEventCode(code: string): boolean {
  return Object.hasOwn(SENTENCES, code);
}

/** The codes the catalogue knows, for tests and for the docs. */
export const KNOWN_EVENT_CODES: readonly string[] = Object.keys(SENTENCES);

/**
 * The event's sentence. An unknown code shows itself (with its table, when it has one), so a
 * code the catalogue has not met is never hidden and never guessed at.
 */
export function eventSentence(row: RunEventRow, scope: EventScope = {}): string {
  const build = Object.hasOwn(SENTENCES, row.event) ? SENTENCES[row.event] : undefined;
  if (!build) return row.entityName ? `${row.entityName}: ${row.event}` : row.event;
  return build(row, payloadOf(row), scope);
}

/** A credential field's label and what its version id is not (#2384). */
const CREDENTIAL_FIELD_WORDS: Record<string, { label: string; kind: string }> = {
  apiKey: { label: "API key", kind: "key" },
  password: { label: "Password", kind: "password" },
  privateKey: { label: "Private key", kind: "key" },
  keyPassphrase: { label: "Key passphrase", kind: "passphrase" },
  clientSecret: { label: "Client secret", kind: "secret" },
  secret: { label: "Secret", kind: "secret" },
  token: { label: "Token", kind: "token" },
  accessToken: { label: "Access token", kind: "token" },
  refreshToken: { label: "Refresh token", kind: "token" },
  personalAccessToken: { label: "Personal access token", kind: "token" },
  // The ids the load and the dbt step write (SnowflakeClient onResolved, DBT_CREDENTIAL_IDS).
  snowflakePrivateKey: { label: "Snowflake private key", kind: "key" },
  snowflakeKeyPassphrase: { label: "Snowflake key passphrase", kind: "passphrase" },
  gitToken: { label: "dbt repository token", kind: "token" },
};

function fieldWords(field: string): { label: string; kind: string } {
  const known = CREDENTIAL_FIELD_WORDS[field];
  if (known) return known;
  const spaced = field
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  const label = spaced === "" ? field : spaced.charAt(0).toUpperCase() + spaced.slice(1);
  return { label, kind: "credential" };
}

/** A 32-character version id as its first six and last four characters. */
function shortVersionId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id;
}

/**
 * The readable lines of a SECRET_VERSIONS_USED payload, one per credential (#2384): a label for
 * the field and the Key Vault version id as an identifier, never as the credential. A reader
 * below `SECRET_POINTER_PERMISSION` gets `(hidden)` in place of the id (#444).
 */
export function secretVersionLines(payload: unknown): string[] {
  if (payload === null || typeof payload !== "object" || Array.isArray(payload)) return [];
  const versions = (payload as Record<string, unknown>).keyVaultVersions;
  if (versions === null || typeof versions !== "object" || Array.isArray(versions)) return [];
  return Object.entries(versions as Record<string, unknown>).map(([field, value]) => {
    const { label, kind } = fieldWords(field);
    const id = typeof value === "string" ? value : "";
    if (id === "(hidden)") return `${label}: Key Vault version hidden for your role.`;
    if (id === "") return `${label}: Key Vault version not recorded.`;
    return `${label}: Key Vault version ${shortVersionId(id)}. This is an identifier, not the ${kind}.`;
  });
}
